import type { WebFilter, WebFilterChain } from '../../web/server/types.ts';
import type { ServerWebExchange } from '../../../types/web/server';
import { DefaultWebFilterChain } from '../../web/server/handler.ts';
import { HttpStatus } from '../../http/status.ts';
import type { ServerWebExchangeMatcher } from '../util/matchers.ts';

export class WebFilterChainProxy implements WebFilter {
    #filters: SecurityWebFilterChain[]
    #firewall: ServerWebExchangeFirewall = new StrictServerWebExchangeFirewall();
    #decorator: WebFilterChainDecorator = new DefaultWebFilterChainDecorator();
    #exchangeRejectedHandler: ServerExchangeRejectedHandler = new HttpStatusExchangeRejectedHandler();

    constructor(...filters: SecurityWebFilterChain[]) {
        this.#filters = [...filters];
    }

    async filter(exchange: ServerWebExchange, chain: WebFilterChain): Promise<void> {
        const firewalledExchange = await this.#firewall.firewalledExchange(exchange);
        try {
            await this.#filterFirewalledExchange(firewalledExchange, chain);
        }
        catch (e) {
            if (e instanceof ServerExchangeRejectedError) {
                await this.#exchangeRejectedHandler.handle(exchange, e)
            }
            else {
                throw e;
            }
        }
    }

    async #filterFirewalledExchange(firewalledExchange: ServerWebExchange, chain: WebFilterChain) {
        for (const securityFilterChain of this.#filters) {
            if (await securityFilterChain.matches(firewalledExchange)) {
                const filters = await securityFilterChain.webFilters();
                const currentChain = this.#decorator.decorate(chain, filters);
                await currentChain.filter(firewalledExchange);
                return;
            }
        }
        await this.#decorator.decorate(chain).filter(firewalledExchange);
    }

    set firewall(firewall: ServerWebExchangeFirewall) {
        if (!firewall) {
            throw new Error('firewall must be provided');
        }
        this.#firewall = firewall;
    }

    set decorator(decorator: WebFilterChainDecorator) {
        if (!decorator) {
            throw new Error('decorator must be provided');
        }
        this.#decorator = decorator;
    }

    set exchangeRejectedHandler(handler: ServerExchangeRejectedHandler) {
        if (!handler) {
            throw new Error('handler must be provided');
        }
        this.#exchangeRejectedHandler = handler;
    }
}

export interface WebFilterChainDecorator {
    decorate(chain: WebFilterChain, filters?: WebFilter[]): WebFilterChain;
}

class DefaultWebFilterChainDecorator implements WebFilterChainDecorator {
    decorate(original: WebFilterChain, filters?: WebFilter[]): WebFilterChain {
        filters = filters || [];
        return DefaultWebFilterChain.of({handle: (exchange) => original.filter(exchange) }, ...filters);
    }
}
export interface SecurityWebFilterChain {
    matches(exchange: ServerWebExchange): Promise<boolean>;
    webFilters(): Promise<WebFilter[]>;
}
export class MatcherSecurityWebFilterChain implements SecurityWebFilterChain {
    readonly #matcher: ServerWebExchangeMatcher
    readonly #filters: WebFilter[];

    constructor(matcher: ServerWebExchangeMatcher, ...filters: WebFilter[]) {
        this.#matcher = matcher;
        this.#filters = filters;
    }

    async matches(exchange: ServerWebExchange): Promise<boolean> {
        return (await this.#matcher(exchange))?.match === true;
    }
    async webFilters(): Promise<WebFilter[]> {
        return this.#filters;
    }
}

export interface ServerWebExchangeFirewall {
    firewalledExchange(exchange: ServerWebExchange): Promise<ServerWebExchange>;
}
class StrictServerWebExchangeFirewall {
    async firewalledExchange(exchange: ServerWebExchange): Promise<ServerWebExchange> {
        return exchange;
    }
}

export class ServerExchangeRejectedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ServerExchangeRejectedError';
    }
}
export interface ServerExchangeRejectedHandler {
    handle(exchange: ServerWebExchange, error: ServerExchangeRejectedError): Promise<void>;
}

class HttpStatusExchangeRejectedHandler implements ServerExchangeRejectedHandler {
    readonly #status: HttpStatus;
    constructor(status: HttpStatus = HttpStatus.BAD_REQUEST) {
        this.#status = status;
    }
    async handle(exchange: ServerWebExchange, error: ServerExchangeRejectedError): Promise<void> {
        exchange.response.setStatusCode(this.#status);
    }

}
